import * as i0 from '@angular/core';
import { InjectionToken, Injectable, Inject, Component, ChangeDetectionStrategy, NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import * as i3$1 from '@angular/material/icon';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import * as i2 from '@firestitch/prompt';
import { FsPromptModule } from '@firestitch/prompt';
import { BehaviorSubject, Subject, timer, of } from 'rxjs';
import { map, takeUntil, filter, switchMap, catchError, take, finalize } from 'rxjs/operators';
import { isAfter, isValid, format } from 'date-fns';
import * as i1 from '@angular/common/http';
import * as i3 from '@angular/router';
import { NavigationEnd } from '@angular/router';
import { parse } from '@firestitch/date';

var CompareMethod;
(function (CompareMethod) {
    CompareMethod[CompareMethod["Version"] = 0] = "Version";
    CompareMethod[CompareMethod["Date"] = 1] = "Date";
})(CompareMethod || (CompareMethod = {}));

var UpdateAction;
(function (UpdateAction) {
    UpdateAction[UpdateAction["PromptUpdate"] = 0] = "PromptUpdate";
    UpdateAction[UpdateAction["NavigationUpdate"] = 1] = "NavigationUpdate";
    UpdateAction[UpdateAction["ManualUpdate"] = 2] = "ManualUpdate";
})(UpdateAction || (UpdateAction = {}));

const FS_BUILD_CONFIG = new InjectionToken('fs-build-config');

class FsBuildService {
    constructor(_config, _http, _prompt, _router) {
        this._config = _config;
        this._http = _http;
        this._prompt = _prompt;
        this._router = _router;
        this._build$ = new BehaviorSubject(null);
        this._destroy$ = new Subject();
        this._pendingUpdate = false;
        this._ran = new Date;
        this._config = Object.assign({ interval: 60, path: 'assets/build.json', origin: window.location.origin, updateAction: UpdateAction.PromptUpdate, compareMethod: CompareMethod.Date }, this._config);
    }
    get build() {
        return Object.assign(Object.assign({}, this._build$.getValue()), { ran: this._ran });
    }
    set build(build) {
        build = Object.assign(Object.assign({}, build), { version: build.version ? String(build.version) : null, date: parse(build.date) });
        if (this.hasUpdate(build)) {
            this._pendingUpdate = true;
            switch (this._config.updateAction) {
                case UpdateAction.PromptUpdate:
                    this._processPromptUpdate(build);
                    break;
                case UpdateAction.NavigationUpdate:
                    this._processNavigationUpdate();
                    break;
                case UpdateAction.ManualUpdate:
                    this._processManualUpdate();
                    break;
            }
        }
        this._build$.next(build);
    }
    get build$() {
        return this._build$
            .asObservable()
            .pipe(map(() => {
            return this.build;
        }), takeUntil(this._destroy$));
    }
    listen(config) {
        const interval = ((config === null || config === void 0 ? void 0 : config.interval) || this._config.interval) * 1000;
        const delay = ((config === null || config === void 0 ? void 0 : config.delay) || 0) * 1000;
        timer(delay, interval)
            .pipe(filter(() => !this._pendingUpdate), switchMap(() => this.get()
            .pipe(catchError(() => of(null)))), filter((data) => !!data), takeUntil(this._destroy$))
            .subscribe((data) => {
            this.build = data;
        });
    }
    hasUpdate(build) {
        var _a, _b;
        if (this._config.compareMethod === CompareMethod.Date) {
            return ((_a = this.build) === null || _a === void 0 ? void 0 : _a.date) && isAfter(build.date, this.build.date);
        }
        if (this._config.compareMethod === CompareMethod.Version) {
            return ((_b = this.build) === null || _b === void 0 ? void 0 : _b.version) && build.version !== this.build.version;
        }
        return false;
    }
    get() {
        const url = new URL(this._config.origin);
        url.pathname = this._config.path;
        const config = {
            headers: null,
        };
        return this._http.get(url.toString(), config);
    }
    ngOnDestroy() {
        this._destroy$.next();
        this._destroy$.complete();
    }
    _processManualUpdate() {
        this._prompt.confirm({
            title: 'New App Version Available',
            template: `The app requires an update`,
            escape: false,
            buttons: [
                {
                    label: 'Update Now',
                },
            ]
        })
            .pipe(takeUntil(this._destroy$))
            .subscribe(() => {
            if (this._config.updateClick) {
                this._config.updateClick(this.build);
            }
        });
    }
    _processNavigationUpdate() {
        this._router.events
            .pipe(filter((event) => event instanceof NavigationEnd), take(1), takeUntil(this._destroy$))
            .subscribe(this._reload);
    }
    _processPromptUpdate(data) {
        const message = data.version ? `Newer version ${data.version} of this app is available` : 'There is a newer version of this app available';
        this._prompt
            .confirm({
            title: 'New App Version Available',
            template: `${message}. Would you like to update now?`,
        })
            .pipe(finalize(() => {
            this._pendingUpdate = false;
        }), takeUntil(this._destroy$))
            .subscribe(() => {
            this._reload();
        });
    }
    _reload() {
        window.location.replace(window.location.href);
    }
}
FsBuildService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsBuildService, deps: [{ token: FS_BUILD_CONFIG }, { token: i1.HttpClient }, { token: i2.FsPrompt }, { token: i3.Router }], target: i0.ɵɵFactoryTarget.Injectable });
FsBuildService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsBuildService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsBuildService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [FS_BUILD_CONFIG]
                }] }, { type: i1.HttpClient }, { type: i2.FsPrompt }, { type: i3.Router }]; } });

class FsBuildComponent {
    constructor(_buildService, _prompt) {
        this._buildService = _buildService;
        this._prompt = _prompt;
        this._destroy$ = new Subject();
    }
    ngOnInit() {
        this._build = this._buildService.build;
        this._buildService.build$
            .pipe(takeUntil(this._destroy$))
            .subscribe((build) => {
            this._build = build;
        });
    }
    ngOnDestroy() {
        this._destroy$.next();
        this._destroy$.complete();
    }
    open() {
        this._prompt.confirm({
            title: 'Build Info',
            template: this._getBuildInfo(),
            class: 'fs-build-overlay-pane',
            buttons: [
                {
                    label: 'Done',
                    color: 'primary'
                }
            ]
        });
    }
    _getBuildInfo() {
        const parts = [];
        if (this._build) {
            if (this._build.name) {
                parts.push('Name: ' + this._build.name);
            }
            if (this._build.version) {
                parts.push('Version: ' + this._build.version);
            }
            if (isValid(new Date(this._build.date))) {
                parts.push('Date: ' + format(new Date(this._build.date), 'PPpp'));
            }
        }
        return parts.join('<br>');
    }
}
FsBuildComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsBuildComponent, deps: [{ token: FsBuildService }, { token: i2.FsPrompt }], target: i0.ɵɵFactoryTarget.Component });
FsBuildComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.16", type: FsBuildComponent, selector: "fs-build", ngImport: i0, template: "\n<div class=\"build\" (click)=\"open()\"><mat-icon>build</mat-icon></div>", styles: [".build{position:fixed;bottom:5px;left:9px;color:#fff;cursor:pointer}.build mat-icon{height:23px;width:23px;font-size:23px}::ng-deep .fs-build-overlay-pane{width:auto!important}::ng-deep .fs-build-overlay-pane .mat-dialog-content{line-height:22px}\n"], components: [{ type: i3$1.MatIcon, selector: "mat-icon", inputs: ["color", "inline", "svgIcon", "fontSet", "fontIcon"], exportAs: ["matIcon"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsBuildComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'fs-build',
                    templateUrl: 'build.component.html',
                    styleUrls: ['build.component.scss'],
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], ctorParameters: function () { return [{ type: FsBuildService }, { type: i2.FsPrompt }]; } });

class FsBuildModule {
}
FsBuildModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsBuildModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
FsBuildModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsBuildModule, declarations: [FsBuildComponent], imports: [CommonModule,
        FsPromptModule,
        MatTooltipModule,
        MatIconModule], exports: [FsBuildComponent] });
FsBuildModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsBuildModule, imports: [[
            CommonModule,
            FsPromptModule,
            MatTooltipModule,
            MatIconModule
        ]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsBuildModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        FsPromptModule,
                        MatTooltipModule,
                        MatIconModule
                    ],
                    exports: [
                        FsBuildComponent,
                    ],
                    declarations: [
                        FsBuildComponent,
                    ]
                }]
        }] });

/*
 * Public API Surface of fs-menu
 */

/**
 * Generated bundle index. Do not edit.
 */

export { CompareMethod, FS_BUILD_CONFIG, FsBuildComponent, FsBuildModule, FsBuildService, UpdateAction };
